<script setup lang="ts">
import Pdf from "@/components/Pdf.vue"
import type { AuditPersonUploadTask } from "@/model/app/audit-person-upload-task"
import type { FrameworkAgreementHolder } from "@/model/app/framework-agreement-holder"
import { GovernedDocumentApprovalState } from "@/model/app/governed-document-approval-state"
import type { PersonInterface } from "@/model/app/person-interface"
import { renderConnectionStatus } from "@/pages/App/OrganizationMember/renderConnectionStatus"
import { $date, sleep, useHttpClient } from "@/vf"
import { computed, reactive, ref, toRefs } from "vue"
import { type TasksProps } from "./tasks-props"

const props = defineProps<TasksProps<AuditPersonUploadTask>>()
const emit = defineEmits<{
    (e: "updated"): void
}>()

const { task } = toRefs(props)

const http = useHttpClient()
const updateErrors = ref<string[]>([])

async function save() {
    const errors = []
    if (props.task.updateFrameworkAgreement && newStatus.frameworkAgreement == "ignore") {
        errors.push("frameworkAgreement")
    }
    if (props.task.updatePictureApproval && newStatus.pictureApproval == "ignore") {
        errors.push("picture")
    }
    if (errors.length > 0) {
        for (let i = 0; i < 3; i++) {
            updateErrors.value = errors
            await sleep(200)
            updateErrors.value = []
            await sleep(200)
        }
        return
    }

    await http.post(`/task/audit-person-upload/${props.task.id}/save`, {
        documentPageAssignment: task.value.documentPageAssignment,
        ...newStatus,
    })

    emit("updated")
}

const uploadedFile = computed(() => props.task.attachments?.find(a => a.originalFilename == "uploaded-file.pdf"))

type DocumentAction = "ignore" | "set" | "unset"
function nextStatus(currentStatus: DocumentAction, statusAfterIgnore: DocumentAction = "set") {
    switch (currentStatus) {
        case "set":
            return "unset"
        case "unset":
            return "ignore"
        case "ignore":
            return statusAfterIgnore
    }
}

const newStatus = reactive<{
    pictureApproval: DocumentAction
    frameworkAgreement: DocumentAction
}>({
    pictureApproval: "ignore",
    frameworkAgreement: "ignore",
})
if (task.value.updatePersonData) {
    newStatus.pictureApproval = "unset"
    newStatus.frameworkAgreement = "unset"
}

const account = computed(() => props.task.account as PersonInterface & FrameworkAgreementHolder)
</script>

<template>
    <div class="p-4 bg-white">
        <div class="row">
            <div class="col-md-6 align-self-stretch">
                <div class="mt-3" v-if="uploadedFile">
                    <pdf :file="`/tasks/task/${task.id}/attachment/${uploadedFile.id}/download`"></pdf>
                </div>
                <div v-else class="h-100 text-center d-flex justify-content-center flex-column">
                    <div>
                        {{ $t("@tasks:tasks.audit_athlete_upload.no_file") }}
                    </div>
                </div>
            </div>
            <div class="col-md-6">
                <div v-if="viewMode === 'admin' && uploadedFile">
                    <b>{{ $t("@tasks:tasks.audit_athlete_upload.page_assignment") }}</b>
                    <table class="table">
                        <thead>
                            <tr>
                                <th>{{ $t("@tasks:tasks.audit_athlete_upload.page_assignment_table.pdfPage") }}</th>
                                <th>{{ $t("@tasks:tasks.audit_athlete_upload.page_assignment_table.qrName") }}</th>
                                <th>{{ $t("@tasks:tasks.audit_athlete_upload.page_assignment_table.qrVersion") }}</th>
                                <th>{{ $t("@tasks:tasks.audit_athlete_upload.page_assignment_table.qrPage") }}</th>
                                <th>{{ $t("@tasks:tasks.audit_athlete_upload.page_assignment_table.assignment") }}</th>
                            </tr>
                        </thead>
                        <tr v-for="(page, index) of task.documentPageAssignment" :key="index">
                            <td>{{ page.pdfPage }}</td>
                            <td>{{ page.conditionName }}</td>
                            <td>{{ page.conditionVersion }}</td>
                            <td>{{ page.uuidPage }}</td>
                            <td>
                                <select
                                    class="form-control"
                                    v-model="page.assignment"
                                    :disabled="task.state === 'closed' || task.state === 'canceled'"
                                >
                                    <option :value="undefined">Ignorieren</option>
                                    <option value="other">Sonstiges</option>
                                    <option value="General.Athletes">Rahmenvereinbarung</option>
                                    <option value="Athlete.PictureApproval">Fotofreigabe</option>
                                </select>
                                <br />
                                <input
                                    class="form-control"
                                    v-model="page.description"
                                    v-if="page.assignment === 'other'"
                                    placeholder="Beschreibung"
                                />
                            </td>
                        </tr>
                    </table>
                </div>
                <div>
                    <b>{{ $t("@tasks:tasks.audit_athlete_upload.member_data") }}</b>
                    <table class="table">
                        <thead>
                            <tr v-if="task.updatePersonData">
                                <td></td>
                                <td>
                                    <b>{{ $t("@tasks:tasks.audit_athlete_upload.personData.old") }}</b>
                                </td>
                                <td>
                                    <b>{{ $t("@tasks:tasks.audit_athlete_upload.personData.new") }}</b>
                                </td>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>{{ $t("@tasks:tasks.audit_athlete_upload.firstName") }}</td>
                                <td>{{ account.firstName }}</td>
                                <td
                                    v-if="task.updatePersonData"
                                    :class="{
                                        'text-danger': account.firstName != task.changedPersonData.firstName,
                                    }"
                                >
                                    {{ task.changedPersonData.firstName }}
                                </td>
                            </tr>
                            <tr>
                                <td>{{ $t("@tasks:tasks.audit_athlete_upload.lastName") }}</td>
                                <td>{{ account.lastName }}</td>
                                <td
                                    v-if="task.updatePersonData"
                                    :class="{
                                        'text-danger': account.lastName != task.changedPersonData.lastName,
                                    }"
                                >
                                    {{ task.changedPersonData.lastName }}
                                </td>
                            </tr>
                            <tr>
                                <td>{{ $t("@tasks:tasks.audit_athlete_upload.gender") }}</td>
                                <td>{{ account.gender == "m" ? "male" : "female" }}</td>
                                <td
                                    v-if="task.updatePersonData"
                                    :class="{
                                        'text-danger': account.gender != task.changedPersonData.gender,
                                    }"
                                >
                                    {{ task.changedPersonData.gender == "m" ? "male" : "female" }}
                                </td>
                            </tr>
                            <tr>
                                <td>{{ $t("@tasks:tasks.audit_athlete_upload.birthDate") }}</td>
                                <td>{{ $date(account.birthDate) }}</td>
                                <td
                                    v-if="task.updatePersonData"
                                    :class="{
                                        'text-danger':
                                            $date(account.birthDate) != $date(task.changedPersonData.birthDate),
                                    }"
                                >
                                    {{ $date(task.changedPersonData.birthDate) }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div class="my-3">
                    <b>{{ $t("@tasks:tasks.audit_athlete_upload.connections") }}</b>
                    <br />
                    <component :is="() => renderConnectionStatus(account, $t)"></component>
                </div>
                <b>{{ $t("@tasks:tasks.audit_athlete_upload.comment") }}</b>
                <div class="bg-light border-primary border-bottom p-3">
                    {{ task.comment || "-" }}
                </div>
                <div v-if="viewMode === 'admin'" class="mt-3">
                    <b>{{ $t("@tasks:tasks.audit_athlete_upload.actions") }}</b>
                    <table class="my-3">
                        <thead>
                            <tr>
                                <th></th>
                                <th>{{ $t("@tasks:tasks.audit_athlete_upload.current_status") }}</th>
                                <th>{{ $t("@tasks:tasks.audit_athlete_upload.requested_status") }}</th>
                                <th v-if="task.state === 'open'">
                                    {{ $t("@tasks:tasks.audit_athlete_upload.set_status") }}
                                </th>
                                <th v-else>{{ $t("@tasks:tasks.audit_athlete_upload.new_status") }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr class="text-center">
                                <td class="text-left">{{ $t("@tasks:tasks.audit_athlete_upload.privacy") }}</td>
                                <td>
                                    <i
                                        :class="{
                                            'doc-icon-status-active':
                                                account.frameworkAgreement?.approvalState ===
                                                GovernedDocumentApprovalState.Approved,
                                            'doc-icon-status-inactive':
                                                account.frameworkAgreement?.approvalState !==
                                                GovernedDocumentApprovalState.Approved,
                                        }"
                                    ></i>
                                </td>
                                <td>
                                    <i
                                        :class="{
                                            'doc-icon-status-active': task.updateFrameworkAgreement
                                                ? task.updateFrameworkAgreement == 'update'
                                                : account.frameworkAgreement?.approvalState ===
                                                  GovernedDocumentApprovalState.Approved,
                                            'doc-icon-status-inactive': task.updateFrameworkAgreement
                                                ? task.updateFrameworkAgreement == 'revoke'
                                                : account.frameworkAgreement?.approvalState !==
                                                  GovernedDocumentApprovalState.Approved,
                                        }"
                                    ></i>
                                </td>
                                <td v-if="task.state === 'open'">
                                    <div
                                        class="border border-light d-inline-block px-1"
                                        :class="{ 'bg-danger': updateErrors.includes('frameworkAgreement') }"
                                        @click="newStatus.frameworkAgreement = nextStatus(newStatus.frameworkAgreement)"
                                    >
                                        <i
                                            :class="{
                                                'doc-icon-status-active': newStatus.frameworkAgreement == 'set',
                                                'doc-icon-status-inactive': newStatus.frameworkAgreement == 'unset',
                                                'doc-icon-status-none': newStatus.frameworkAgreement == 'ignore',
                                            }"
                                        ></i>
                                    </div>
                                </td>
                                <td v-else>
                                    <i
                                        :class="{
                                            'doc-icon-status-active': task.updateFrameworkAgreementPerformed == 'set',
                                            'doc-icon-status-inactive':
                                                task.updateFrameworkAgreementPerformed == 'unset',
                                            'doc-icon-status-none': task.updateFrameworkAgreementPerformed == 'ignore',
                                        }"
                                    ></i>
                                </td>
                            </tr>
                            <tr class="text-center">
                                <td class="text-left">{{ $t("@tasks:tasks.audit_athlete_upload.picture") }}</td>
                                <td>
                                    <i
                                        :class="{
                                            'doc-icon-status-active':
                                                account.pictureApproval?.approvalState ===
                                                GovernedDocumentApprovalState.Approved,
                                            'doc-icon-status-inactive':
                                                account.pictureApproval?.approvalState !==
                                                GovernedDocumentApprovalState.Approved,
                                        }"
                                    ></i>
                                </td>
                                <td>
                                    <i
                                        :class="{
                                            'doc-icon-status-active': task.updatePictureApproval
                                                ? task.updatePictureApproval == 'update'
                                                : account.pictureApproval?.approvalState ===
                                                  GovernedDocumentApprovalState.Approved,
                                            'doc-icon-status-inactive': task.updatePictureApproval
                                                ? task.updatePictureApproval == 'revoke'
                                                : account.pictureApproval?.approvalState !==
                                                  GovernedDocumentApprovalState.Approved,
                                        }"
                                    ></i>
                                </td>
                                <td v-if="task.state === 'open'">
                                    <div
                                        class="border border-light d-inline-block px-1"
                                        :class="{ 'bg-danger': updateErrors.includes('picture') }"
                                        @click="newStatus.pictureApproval = nextStatus(newStatus.pictureApproval)"
                                    >
                                        <i
                                            :class="{
                                                'doc-icon-status-active': newStatus.pictureApproval == 'set',
                                                'doc-icon-status-inactive': newStatus.pictureApproval == 'unset',
                                                'doc-icon-status-none': newStatus.pictureApproval == 'ignore',
                                            }"
                                        ></i>
                                    </div>
                                </td>
                                <td v-else>
                                    <i
                                        :class="{
                                            'doc-icon-status-active': task.updatePictureApprovalPerformed == 'set',
                                            'doc-icon-status-inactive': task.updatePictureApprovalPerformed == 'unset',
                                            'doc-icon-status-none': task.updatePictureApprovalPerformed == 'ignore',
                                        }"
                                    ></i>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <div class="text-right" v-if="task.state === 'open'">
                        <button class="btn btn-primary" @click="save()">
                            {{ $t("@tasks:tasks.audit_athlete_upload.save") }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
